'use client';

import { useEffect, useMemo, useState } from 'react';
import useMediaQuery from '@fe-news/hooks/use-media-query';
import { SCREEN_SIZE } from '@fe-common-utils/constants/screen';
import {
  SMART_BANNER_CLOSE_EVENT,
  SMART_BANNER_HEIGHT,
  loadSmartBannerIsCloseFromCookie
} from 'fe-common-library-v2/components';
import { usePathname } from 'next/navigation';

interface DesktopWithSidebarContainerProps {
  children?: React.ReactNode;
}

const DesktopWithSidebarContainer = ({ children }: DesktopWithSidebarContainerProps) => {
  const isDesktop = useMediaQuery(`(min-width:${SCREEN_SIZE.DESKTOP}px)`, true);
  const pathname = usePathname() ?? '';
  // 新聞詳情頁在手機版除了 Header / SmartBanner 外，還會多一層 AnueTopBar 分享列，因此內容需再預留 48px。
  const isNewsDetail = pathname.includes('/news/id/');
  // 這個狀態只在手機版使用：用來把內容往下推，避免首屏廣告被 SmartBanner 或 AnueTopBar 蓋住。
  const [isShowSmartBanner, setIsShowSmartBanner] = useState(false);

  useEffect(() => {
    if (isDesktop) {
      // 桌機版沒有 SmartBanner 造成的內容位移問題，明確歸零可避免從手機切回桌機時殘留 padding。
      setIsShowSmartBanner(false);
      return;
    }

    // 與 fe-common-library-v2 的 SmartBanner 使用同一顆 cookie 判斷是否已關閉，確保佔位和實際 Banner 顯示同步。
    setIsShowSmartBanner(!loadSmartBannerIsCloseFromCookie());

    const onCloseSmartBanner = () => {
      setIsShowSmartBanner(false);
    };

    // 使用共用元件拋出的關閉事件更新本地狀態，讓使用者關閉 Banner 後內容可以立刻回到正確位置。
    window.addEventListener(SMART_BANNER_CLOSE_EVENT, onCloseSmartBanner);

    return () => {
      window.removeEventListener(SMART_BANNER_CLOSE_EVENT, onCloseSmartBanner);
    };
  }, [isDesktop]);

  const contentMainNewsStyle = useMemo(() => {
    if (isDesktop) return;

    // 手機版的上方保留高度：
    // - 新聞詳情：AnueTopBar 48px + SmartBanner 54px (若顯示)
    // - 其他頁面：只需要 SmartBanner 54px (若顯示)
    // 這裡直接以 inline style 寫入，避免等待外層 Header 的 DOM 同步時發生首屏廣告被蓋住。
    return {
      paddingTop: isNewsDetail
        ? `${(isShowSmartBanner ? SMART_BANNER_HEIGHT : 0) + 48}px`
        : isShowSmartBanner
          ? `${SMART_BANNER_HEIGHT}px`
          : '0px'
    };
  }, [isDesktop, isNewsDetail, isShowSmartBanner]);

  const contentMainNewsClassName = useMemo(() => {
    if (isDesktop) return;

    // 同步寫入 className 作為 CSS fallback：若 inline style 還沒套上，global.css 仍能用這些 class 補上正確 padding。
    return [
      'content-main-news',
      isNewsDetail && 'content-main-news-detail',
      isShowSmartBanner && 'content-main-news-with-smart-banner'
    ]
      .filter(Boolean)
      .join(' ');
  }, [isDesktop, isNewsDetail, isShowSmartBanner]);

  return (
    <div id={isDesktop ? '' : 'content-main-news'} className={contentMainNewsClassName} style={contentMainNewsStyle}>
      {children}
    </div>
  );
};

export default DesktopWithSidebarContainer;
